import ko from 'knockout';

import type { IsUnion } from '@/util/types';

ko.utils.beforeWrite = function <T>(obs: ko.Subscribable<T>, fn: (val: T) => T) {
    const ret = function (this: typeof obs) {
        return arguments.length ? obs.call(this, fn(arguments[0])) : obs.call(this);
    };
    Object.setPrototypeOf(ret, obs);
    return ret as ko.Observable;
};
declare module 'knockout' {
    namespace utils {
        function beforeWrite<T>(obs: Subscribable<T>, fn: (val: T) => T): Observable<T>;
    }
}

ko.utils.waitUntil = async function <R>(fn: () => R, { timeout }: { timeout?: number } = {}): Promise<R> {
    let computed: ko.Computed, timer: number;
    return new Promise<R>((resolve, reject) => {
        // If fn() throws on its first invokation the computed is never created. We use a separate observable to ensure
        // fn() is only invoked once the computed is successfully created.
        const active = ko.observable(false);
        computed = ko.computed(() => {
            const res = active() && fn();
            if (res) {
                resolve(res);
            }
        });
        if (timeout) {
            timer = window.setTimeout(() => reject(new Error('ko.utils.waitUntil timeout')), timeout);
        }
        queueMicrotask(() => active(true));
    }).finally(() => {
        computed.dispose();
        if (timer) {
            window.clearTimeout(timer);
        }
    });
};
declare module 'knockout' {
    namespace utils {
        function waitUntil<R>(fn: () => R, { timeout }?: { timeout?: number }): Promise<R>;
    }
}

/**
 * Map the given object so that the deepest properties are guaranteed to be observables.
 * @param obj - The object to map.
 * @param options - Additional options.
 * @param options.force_observables - Force observables all the way down.
 */
ko.utils.quickmap = (obj: unknown, { force_observables = false } = {}) =>
    // eslint-disable-next-line typescript/no-explicit-any
    (function quickmap(o: any, nowrap?: boolean): any {
        const type = typeof o;
        if ((type !== 'object' || !o || (o.constructor !== Object && o.constructor !== Array)) && type !== 'function') {
            return nowrap ? o : ko.observable(o);
        }
        if (type === 'object') {
            if (Array.isArray(o)) {
                const arr = o.map(e => quickmap(e, !force_observables));
                return nowrap ? arr : ko.observableArray(arr);
            }
            const ret: Record<string, unknown> = {};
            Object.keys(o).forEach(k => {
                const mapped = quickmap(o[k]);
                ret[k] = force_observables && !ko.isObservable(mapped) ? ko.observable(mapped) : mapped;
            });
            return ret;
        }
        return o;
    })(obj);
declare module 'knockout' {
    namespace utils {
        type QuickMap<O, F extends boolean = false, NW extends boolean = false> = QMap<O, F, NW>;
        function quickmap<O, F extends boolean>(
            obj: O,
            { force_observables }?: { force_observables?: F },
        ): QuickMap<O, F>;
    }
}

type ArrayIfOnlyArray<T> = IsUnion<T> extends true ? unknown : [T] extends [readonly unknown[]] ? T : unknown;

type Not<B extends boolean> = B extends true ? false : true;
type QMap<O, F extends boolean = false, NW extends boolean = false> =
    // eslint-disable-next-line typescript/no-explicit-any
    [O] extends [(...args: any[]) => any]
        ? O
        : ArrayIfOnlyArray<Exclude<O, undefined>> extends readonly (infer U)[]
          ? NW extends true
              ? QMap<U, F, Not<F>>[] | Extract<O, undefined>
              : ko.ObservableArray<QMap<U, F, Not<F>>> | Extract<O, undefined>
          : Exclude<O, undefined> extends Record<string, unknown>
            ? { [K in keyof O]: QMapObjectVal<O[K], F> }
            : NW extends true
              ? O
              : ko.Observable<O>;
type QMapObjectVal<V, F extends boolean, M = QMap<V, F>> = F extends true
    ? Exclude<M, undefined> extends ko.Observable
        ? M
        : ko.Observable<M>
    : M;
